// Reproductor PÚBLICO de Digital Signage por token (/signage/tv/:token — fuera
// del login). El box Android abre esta URL en kiosko.
//
// OFFLINE (Milestone 2b): el app-shell ya lo precachea vite-plugin-pwa (workbox),
// así la página abre sin red tras la 1ª visita. El CONTENIDO (sig_reproducir, que
// trae catálogo con imágenes como data URI + tema + escenas) se guarda en
// IndexedDB (offline.ts kv) por token; si la red cae, se sirve el último contenido
// y el reproductor sigue en bucle. Al reconectar, sondea la firma y recarga solo
// si cambió; el latido + proof-of-play se reintenta.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MonitorX, WifiOff } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { cacheGet, cacheSet, conexion } from '@/features/pos/offline';
import { SignagePlayer, playerCss } from './player';
import type { Anuncio, Categoria, Combo, Cupon, Escena, FeedFuente, GadgetsCfg, Phygital, ResenasData, SaludData, SceneInfo, SocialCuenta, TemaTokens, TurnoMedico, VencItem } from './player';

// El catálogo del reproductor trae imagen_ver (hash/ETag), no la foto base64.
type RawProd = { id: string; nombre: string; descripcion: string | null; marca: string | null; precio: number; iva_tarifa: number | null; controla_stock: boolean; stock: number | null; agotado: boolean; imagen_ver: string | null; precio_antes?: number | null; descuento?: number | null; etiqueta?: string | null; etiqueta_color?: string | null; web_slug?: string | null };
type RawCat = { categoria: string; n: number; productos: RawProd[] };
type RawComboItem = { id: string; nombre: string; marca: string | null; cantidad: number; precio: number; imagen_ver: string | null };
type RawCombo = { id: string; nombre: string; etiqueta: string | null; descripcion: string | null; descuento: number; imagen: string | null; items: RawComboItem[]; suma: number; precio_pack: number; ahorro: number };
type RawVenc = { id: string; nombre: string; marca: string | null; dias: number; lote: string | null; stock: number | null; precio: number; precio_antes: number | null; descuento: number; imagen_ver: string | null };
type RawSaludProd = { id: string; nombre: string; marca: string | null; precio: number; precio_antes: number | null; descuento: number; imagen_ver: string | null };
type RawSaludTip = { texto: string; icono: string | null; condicion: string; cta: string | null; producto: RawSaludProd | null };
type RawSalud = { tips: RawSaludTip[]; lat: number | null; lon: number | null };

interface Reproducir {
  valido: boolean;
  pantalla?: { id: string; nombre: string; orientacion: string; resolucion: string | null; modo?: string };
  empresa?: string | null; local?: string | null;
  tema?: { preset: string | null; tokens: TemaTokens | null; logo: string | null } | null;
  plantilla?: { id: string; nombre: string; sync?: boolean } | null;
  escenas?: Escena[]; catalogo?: RawCat[]; top_ventas?: RawCat[]; lentos?: RawCat[]; combos?: RawCombo[]; cupones?: Cupon[]; por_vencer?: RawVenc[]; salud?: RawSalud | null; social?: SocialCuenta[];
  ventas_hoy?: RawCat[]; turnos?: TurnoMedico[]; lat?: number | null; lon?: number | null; resenas?: ResenasData | null; gadgets?: GadgetsCfg | null; firma?: string; refresco_seg?: number;
  campana?: { codigo: string; efecto: string; intensidad?: number; acento?: string | null; saludo?: string | null } | null;
  anuncios?: Anuncio[];
  phygital?: Phygital | null;
  feeds?: FeedFuente[];
  patrocinado?: boolean;
  avisos?: string[];
}

function useOnline() {
  const [on, setOn] = useState(conexion.online);
  useEffect(() => conexion.suscribir(setOn), []);
  return on;
}

// ---- Fully Kiosk Browser (script 418) ---------------------------------------
// Dentro de Fully la página recibe window.fully (JS API): salud del box en el
// latido + comandos que el navegador pelado no puede (screenshot real, restart
// de la app, reboot del equipo). Requiere en Fully: Advanced Web Settings →
// "Enable JavaScript Interface" (y PLUS para reboot). Sin Fully todo degrada.
interface FullyApi {
  getBatteryLevel?: () => number; isPlugged?: () => boolean; getWifiSignalLevel?: () => number;
  getIp4Address?: () => string; getFullyVersion?: () => string; getDeviceModel?: () => string;
  getScreenshotPngBase64?: () => string; restartApp?: () => void; rebootDevice?: () => void;
  clearCache?: () => void;
}
declare global { interface Window { fully?: FullyApi } }

function saludBox(): Record<string, unknown> {
  const f = window.fully;
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
  const s: Record<string, unknown> = {
    kiosk: !!f,
    uptime_min: Math.round(performance.now() / 60000),
    ...(mem ? { heap_mb: Math.round(mem.usedJSHeapSize / 1048576) } : {}),
  };
  try {
    if (f) {
      Object.assign(s, {
        bateria: f.getBatteryLevel?.(), enchufado: f.isPlugged?.(), wifi: f.getWifiSignalLevel?.(),
        ip: f.getIp4Address?.(), app: f.getFullyVersion?.(), modelo: f.getDeviceModel?.(),
      });
    }
  } catch { /* la JS Interface puede estar deshabilitada */ }
  return s;
}

// Screenshot de Fully (PNG full-res) → JPEG ~800px para no engordar la BD.
async function capturaReducida(): Promise<string | null> {
  const b64 = window.fully?.getScreenshotPngBase64?.();
  if (!b64) return null;
  const src = b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const esc = Math.min(1, 800 / img.width);
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * esc); c.height = Math.round(img.height * esc);
      c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/jpeg', 0.7));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// ---- Pantalla de cliente en caja (modo='caja', script 412) ------------------
// Sondea sig_contexto_caja: con carrito en curso muestra la compra al cliente
// (líneas + totales + QR De Una + gracias/vuelto); ociosa vuelve a la playlist.
// LOPDP: el payload NO trae nombre de cliente ni datos de lealtad.
interface CajaLinea { descripcion: string; cantidad: number; precio: number; subtotal: number }
interface CajaDatos { estado?: string; lineas?: CajaLinea[]; subtotal?: number; descuento?: number; iva?: number; total?: number; vuelto?: number; qr?: string | null }

function useContextoCaja(token: string | undefined, activo: boolean) {
  const [ctx, setCtx] = useState<CajaDatos | null>(null);
  useEffect(() => {
    if (!token || !activo) { setCtx(null); return; }
    let cancel = false;
    const tick = async () => {
      try {
        const r = (await apiClient.post('/rpc/sig_contexto_caja', { _token: token })).data as { activo: boolean; datos?: CajaDatos };
        if (!cancel) setCtx(r?.activo ? (r.datos ?? null) : null);
      } catch { if (!cancel) setCtx(null); }   // sin red → playlist (nunca datos viejos de otro cliente)
    };
    void tick();
    const id = setInterval(() => void tick(), 3000);
    return () => { cancel = true; clearInterval(id); };
  }, [token, activo]);
  return ctx;
}

const cajaCss = `
.sigcx{ position:relative; width:100%; height:100%; container-type:size; overflow:hidden; display:flex; flex-direction:column;
  color:var(--ink); background:var(--paper); font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
  --paper:#efe7da; --paper2:#e7ddcc; --ink:#191310; --soft:#6f655a; --accent:#c6303a; --sage:#4f7a52; }
.sigcx-top{ display:flex; align-items:center; justify-content:space-between; padding:3cqh 4cqw 0; }
.sigcx-top img{ height:4.5cqh; width:auto; max-width:24cqw; object-fit:contain; }
.sigcx-top .t{ font-size:1.6cqw; letter-spacing:.22em; text-transform:uppercase; color:var(--soft); font-weight:700; }
.sigcx-body{ flex:1; display:grid; grid-template-columns:1.6fr 1fr; gap:4cqw; padding:3cqh 4cqw 4cqh; min-height:0; }
.sigcx-lineas{ overflow:hidden; display:flex; flex-direction:column; justify-content:flex-end; }
.sigcx-linea{ display:flex; align-items:baseline; gap:1.6cqw; padding:1.1cqh 0; border-bottom:1px solid color-mix(in srgb,var(--ink) 10%,transparent); }
.sigcx-linea .n{ flex:1; font-size:2cqw; font-weight:600; line-height:1.15; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.sigcx-linea .c{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:1.6cqw; color:var(--soft); font-variant-numeric:tabular-nums; }
.sigcx-linea .s{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:2cqw; font-weight:700; font-variant-numeric:tabular-nums; }
.sigcx-tot{ display:flex; flex-direction:column; justify-content:flex-end; gap:1cqh; text-align:right; }
.sigcx-tot .fila{ display:flex; justify-content:space-between; font-size:1.8cqw; color:var(--soft); font-variant-numeric:tabular-nums; }
.sigcx-tot .fila.ahorro{ color:var(--sage); font-weight:700; }
.sigcx-tot .total{ border-top:.3cqh solid var(--ink); padding-top:1.6cqh; margin-top:1cqh; }
.sigcx-tot .total span{ display:block; font-size:1.4cqw; letter-spacing:.24em; text-transform:uppercase; color:var(--soft); }
.sigcx-tot .total b{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:7cqw; font-weight:800; line-height:1.1; color:var(--accent); font-variant-numeric:tabular-nums; }
.sigcx-centro{ flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:2.4cqh; text-align:center; padding:4cqh 6cqw; }
.sigcx-centro .qr{ width:34cqmin; height:34cqmin; background:#fff; border-radius:2cqw; padding:1.6cqmin; box-shadow:0 2cqh 6cqh color-mix(in srgb,var(--ink) 14%,transparent); }
.sigcx-centro .qr img{ width:100%; height:100%; object-fit:contain; }
.sigcx-centro h2{ font-family:Georgia,"Times New Roman",serif; font-size:4cqw; font-weight:700; margin:0; }
.sigcx-centro .monto{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:5.5cqw; font-weight:800; color:var(--accent); font-variant-numeric:tabular-nums; }
.sigcx-centro .ok{ width:16cqmin; height:16cqmin; border-radius:999px; background:var(--sage); color:#fff; display:flex; align-items:center; justify-content:center; font-size:9cqmin; }
.sigcx-centro .sub{ font-size:1.9cqw; color:var(--soft); }
@container (max-aspect-ratio: 0.9) {
  .sigcx-body{ grid-template-columns:1fr; grid-template-rows:1fr auto; }
}`;

function CajaView({ datos, empresa, logo, tokens }: { datos: CajaDatos; empresa?: string | null; logo?: string | null; tokens?: TemaTokens | null }) {
  const fmt = (n?: number) => `$${(Number.isFinite(n ?? NaN) ? Number(n) : 0).toFixed(2)}`;
  const vars = {
    '--paper': tokens?.paper, '--paper2': tokens?.paper2, '--ink': tokens?.ink,
    '--soft': tokens?.soft, '--accent': tokens?.accent, '--sage': tokens?.sage,
  } as CSSProperties;
  const lineas = (datos.lineas ?? []).slice(-12);   // las últimas caben siempre
  return (
    <div className="sigcx" style={vars}>
      <div className="sigcx-top">
        {logo ? <img src={logo} alt={empresa ?? ''} /> : <b style={{ fontSize: '2cqw' }}>{empresa}</b>}
        <span className="t">{datos.estado === 'pagado' ? 'Gracias por su compra' : datos.estado === 'qr' ? 'Pago con QR' : 'Su compra'}</span>
      </div>
      {datos.estado === 'qr' ? (
        <div className="sigcx-centro">
          {datos.qr && <div className="qr"><img src={datos.qr} alt="QR de pago" /></div>}
          <h2>Escanee para pagar con De Una</h2>
          <div className="monto">{fmt(datos.total)}</div>
        </div>
      ) : datos.estado === 'pagado' ? (
        <div className="sigcx-centro">
          <div className="ok">✓</div>
          <h2>¡Gracias por su compra!</h2>
          <div className="monto">{fmt(datos.total)}</div>
          {(datos.vuelto ?? 0) > 0 && <div className="sub">Su vuelto: <b>{fmt(datos.vuelto)}</b></div>}
        </div>
      ) : (
        <div className="sigcx-body">
          <div className="sigcx-lineas">
            {lineas.map((l, i) => (
              <div key={i} className="sigcx-linea">
                <span className="n">{l.descripcion}</span>
                <span className="c">{l.cantidad} × {fmt(l.precio)}</span>
                <span className="s">{fmt(l.subtotal)}</span>
              </div>
            ))}
          </div>
          <div className="sigcx-tot">
            <div className="fila"><span>Subtotal</span><span>{fmt(datos.subtotal)}</span></div>
            {(datos.descuento ?? 0) > 0.004 && <div className="fila ahorro"><span>Su ahorro</span><span>-{fmt(datos.descuento)}</span></div>}
            <div className="fila"><span>IVA</span><span>{fmt(datos.iva)}</span></div>
            <div className="total"><span>Total a pagar</span><b>{fmt(datos.total)}</b></div>
          </div>
        </div>
      )}
    </div>
  );
}

// Aspecto del viewport (para pantallas verticales sobre boxes que emiten framebuffer horizontal).
function useApaisado() {
  const [wide, setWide] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= window.innerHeight));
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= window.innerHeight);
    window.addEventListener('resize', on);
    window.addEventListener('orientationchange', on);
    return () => { window.removeEventListener('resize', on); window.removeEventListener('orientationchange', on); };
  }, []);
  return wide;
}

export function SignageTV() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const online = useOnline();
  const apaisado = useApaisado();
  const buffer = useRef<{ tipo: string; ref: string | null; titulo: string | null; dur: number }[]>([]);
  const estado = useRef<{ escena: string; titulo: string | null } | null>(null);
  const KEY = `sig_tv:${token}`;

  // Contenido con RESPALDO offline: red → cachea; si falla → último cacheado.
  const { data, refetch } = useQuery<Reproducir>({
    queryKey: ['sig-tv', token],
    enabled: !!token, staleTime: Infinity, retry: false,
    queryFn: async () => {
      try {
        const r = (await apiClient.post('/rpc/sig_reproducir', { _token: token })).data as Reproducir;
        if (r?.valido) void cacheSet(KEY, { data: r, ts: Date.now() });
        return r;
      } catch (e) {
        const c = await cacheGet<{ data: Reproducir }>(KEY);
        if (c?.data) return c.data;   // sin red y sin caché previa → propaga error
        throw e;
      }
    },
  });

  // Ejecutor de comandos remotos (los entrega el latido unificado).
  const ejecutarComandos = useCallback(async (cmds: { id: string; tipo: string }[]) => {
    if (!token || !cmds.length) return;
    const ack = (id: string, resultado: Record<string, unknown>) =>
      apiClient.post('/rpc/sig_comando_ack', { _token: token, _id: id, _resultado: resultado }).catch(() => {});
    for (const c of cmds) {
      try {
        if (c.tipo === 'refrescar') { await refetch(); await ack(c.id, { ok: true }); }
        else if (c.tipo === 'recargar') { await ack(c.id, { ok: true }); window.location.reload(); }
        else if (c.tipo === 'limpiar_cache') {
          if ('caches' in window) { const ks = await caches.keys(); await Promise.all(ks.map((k) => caches.delete(k))); }
          window.fully?.clearCache?.();
          await ack(c.id, { ok: true }); window.location.reload();
        } else if (c.tipo === 'captura') {
          const img = await capturaReducida();
          if (img) await ack(c.id, { ok: true, img });
          else await ack(c.id, { ok: false, msg: 'requiere Fully Kiosk con la JS Interface habilitada' });
        } else if (c.tipo === 'reiniciar' || c.tipo === 'reboot') {
          const f = window.fully;
          const fn = c.tipo === 'reboot' ? f?.rebootDevice : f?.restartApp;
          if (!fn) await ack(c.id, { ok: false, msg: c.tipo === 'reboot' ? 'requiere Fully Kiosk PLUS' : 'requiere Fully Kiosk' });
          else { await ack(c.id, { ok: true }); fn.call(f); }   // ack ANTES: la app muere al ejecutar
        } else { await ack(c.id, { ok: false, msg: 'no soportado en navegador (requiere app kiosko)' }); }
      } catch { await ack(c.id, { ok: false }); }
    }
  }, [token, refetch]);

  // POLLER ÚNICO (421): antes eran 3 sondeos (firma 20s + comandos 15s + latido
  // 30s ≈ 13 req/min por box); ahora UNO cada ~20s (+jitter anti-estampida) hace
  // el latido (estado + salud + proof-of-play) y recibe firma + comandos en la
  // misma respuesta. Si la firma cambió, el refetch pesado espera 0-8s aleatorios
  // (el servidor además memoiza el payload — 414). Los RPCs sueltos siguen
  // existiendo para bundles viejos.
  useEffect(() => {
    if (!token) return;
    const ms = (data?.refresco_seg ?? 20) * 1000 + Math.random() * 4000;
    let tRefetch: ReturnType<typeof setTimeout> | null = null;
    let pendiente = false;
    const tick = async () => {
      const reproducciones = buffer.current.splice(0, buffer.current.length);
      let r: { valido?: boolean; firma?: string; comandos?: { id: string; tipo: string }[] };
      try {
        r = (await apiClient.post('/rpc/sig_latido', {
          _token: token,
          _payload: { version: 'web-2', estado: estado.current, reproducciones, salud: saludBox() },
        })).data;
      } catch { buffer.current.unshift(...reproducciones.slice(-100)); return; }   // sin red: reintenta el POP
      if (r?.firma && data?.firma && r.firma !== data.firma && !pendiente) {
        pendiente = true;
        tRefetch = setTimeout(() => { void refetch(); }, Math.random() * 8000);
      }
      await ejecutarComandos(r?.comandos ?? []);
    };
    const id = setInterval(() => void tick(), ms);
    return () => { clearInterval(id); if (tRefetch) clearTimeout(tRefetch); };
  }, [token, data?.firma, data?.refresco_seg, refetch, ejecutarComandos]);

  // Watchdog: recarga programada (~6h + jitter) para limpiar fugas de memoria en 24/7.
  useEffect(() => {
    const ms = (6 * 3600 + Math.floor(Math.random() * 900)) * 1000;
    const t = setTimeout(() => window.location.reload(), ms);
    return () => clearTimeout(t);
  }, []);

  // Pantalla de cliente en caja: con contexto activo se muestra la compra; ociosa, playlist.
  const modoCaja = data?.pantalla?.modo === 'caja';
  const ctxCaja = useContextoCaja(token, modoCaja && online);

  const onScene = useCallback((s: SceneInfo) => {
    estado.current = { escena: s.tipo, titulo: s.titulo };
    buffer.current.push({ tipo: s.tipo, ref: s.ref, titulo: s.titulo, dur: s.dur });
    if (buffer.current.length > 200) buffer.current.splice(0, buffer.current.length - 200);
  }, []);

  // Resuelve las fotos por VERSIÓN: usa la caché IndexedDB (id:ver) y solo pide a
  // sig_imagenes las que faltan → el JSON del reproductor no carga base64.
  const [imgMap, setImgMap] = useState<Record<string, string>>({});
  useEffect(() => {
    const items: { id: string; imagen_ver: string }[] = [
      ...[...(data?.catalogo ?? []), ...(data?.top_ventas ?? []), ...(data?.lentos ?? []), ...(data?.ventas_hoy ?? [])].flatMap((c) => c.productos),
      ...(data?.combos ?? []).flatMap((c) => c.items),
      ...(data?.por_vencer ?? []),
      ...(data?.salud?.tips ?? []).map((t) => t.producto).filter((p): p is RawSaludProd => !!p),
    ].filter((p) => !!p.imagen_ver).map((p) => ({ id: p.id, imagen_ver: p.imagen_ver as string }));
    if (!items.length) { setImgMap({}); return; }
    let cancel = false;
    (async () => {
      const map: Record<string, string> = {}; const faltan: string[] = []; const verById: Record<string, string> = {};
      for (const it of items) {
        verById[it.id] = it.imagen_ver as string;
        const c = await cacheGet<string>(`sigimg:${it.id}:${it.imagen_ver}`);
        if (c) map[it.id] = c; else faltan.push(it.id);
      }
      if (faltan.length && navigator.onLine) {
        try {
          const rows = (await apiClient.post('/rpc/sig_imagenes', { _token: token, _ids: faltan })).data as { id: string; imagen: string | null }[];
          for (const r of rows) if (r.imagen) { map[r.id] = r.imagen; void cacheSet(`sigimg:${r.id}:${verById[r.id]}`, r.imagen); }
        } catch { /* sin red: usa lo cacheado */ }
      }
      if (!cancel) setImgMap(map);
    })();
    return () => { cancel = true; };
  }, [data?.catalogo, data?.top_ventas, data?.lentos, data?.ventas_hoy, data?.combos, data?.por_vencer, data?.salud, token]);

  // Auto-recuperación de token MUERTO: si el box llegó aquí con el token que
  // guardó el emparejamiento y ya no sirve (pantalla borrada / BD limpiada /
  // token regenerado), se descarta y se vuelve a /signage/pair a mostrar un
  // código nuevo — sin esto el box quedaba atrapado en una pantalla muerta.
  const tokenMuerto = !!data && !data.valido;
  useEffect(() => {
    if (!tokenMuerto) return;
    try {
      if (localStorage.getItem('sig_token') === token) {
        localStorage.removeItem('sig_token');
        navigate('/signage/pair', { replace: true });
      }
    } catch { /* localStorage bloqueado: se muestra el aviso normal */ }
  }, [tokenMuerto, token, navigate]);

  if (data && !data.valido) {
    return (
      <div style={{ width: '100vw', height: '100vh' }} className="flex flex-col items-center justify-center gap-3 bg-black text-white/70">
        <MonitorX className="w-12 h-12" />
        <p>Esta pantalla no está registrada o fue desactivada.</p>
      </div>
    );
  }

  const playerData = {
    empresa: data?.empresa, local: data?.local,
    tema: data?.tema ? { preset: data.tema.preset, tokens: data.tema.tokens, logo: data.tema.logo } : null,
    escenas: data?.escenas,
    categorias: (data?.catalogo ?? []).map((c): Categoria => ({
      categoria: c.categoria, n: c.n,
      productos: c.productos.map((p) => ({ ...p, imagen: imgMap[p.id] ?? null })),
    })),
    topVentas: (data?.top_ventas ?? []).map((c): Categoria => ({
      categoria: c.categoria, n: c.n,
      productos: c.productos.map((p) => ({ ...p, imagen: imgMap[p.id] ?? null })),
    })),
    lentos: (data?.lentos ?? []).map((c): Categoria => ({
      categoria: c.categoria, n: c.n,
      productos: c.productos.map((p) => ({ ...p, imagen: imgMap[p.id] ?? null })),
    })),
    feeds: data?.feeds ?? [],
    combos: (data?.combos ?? []).map((cb): Combo => ({
      ...cb, items: cb.items.map((it) => ({ ...it, imagen: imgMap[it.id] ?? null })),
    })),
    cupones: data?.cupones ?? [],
    avisos: data?.avisos ?? [],
    sync: data?.plantilla?.sync ?? false,
    porVencer: (data?.por_vencer ?? []).map((v): VencItem => ({ ...v, imagen: imgMap[v.id] ?? null })),
    salud: data?.salud ? ({
      lat: data.salud.lat, lon: data.salud.lon,
      tips: data.salud.tips.map((t) => ({ ...t, producto: t.producto ? { ...t.producto, imagen: imgMap[t.producto.id] ?? null } : null })),
    } as SaludData) : null,
    social: data?.social ?? [],
    ventasHoy: (data?.ventas_hoy ?? []).map((c): Categoria => ({
      categoria: c.categoria, n: c.n,
      productos: c.productos.map((p) => ({ ...p, imagen: imgMap[p.id] ?? null })),
    })),
    turnos: data?.turnos ?? [],
    lat: data?.lat ?? null, lon: data?.lon ?? null,
    resenas: data?.resenas ?? null,
    gadgets: data?.gadgets ?? null,
  };

  // Pantalla vertical (9:16) sobre un box que emite framebuffer horizontal:
  // rotamos el escenario 90° para que ocupe el panel en retrato; el reflow del
  // reproductor (container-query 9:16) se activa solo porque el contenedor queda vertical.
  const rotar = data?.pantalla?.orientacion === 'vertical' && apaisado;
  const stageStyle: CSSProperties = rotar
    ? { position: 'absolute', top: '50%', left: '50%', width: '100vh', height: '100vw', transform: 'translate(-50%,-50%) rotate(90deg)', transformOrigin: 'center center' }
    : { width: '100%', height: '100%' };

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#000', overflow: 'hidden' }}>
      <style>{playerCss + cajaCss}</style>
      <div style={stageStyle}>
        {modoCaja && ctxCaja
          ? <CajaView datos={ctxCaja} empresa={data?.empresa} logo={data?.tema?.logo ?? null} tokens={data?.tema?.tokens ?? null} />
          : <SignagePlayer data={playerData} onScene={onScene} campana={data?.campana ?? null} anuncios={data?.anuncios ?? []} phygital={data?.phygital ?? null} patrocinado={!!data?.patrocinado} token={token} pantallaId={data?.pantalla?.id} />}
      </div>
      {!online && (
        <div style={{ position: 'fixed', left: 14, bottom: 14, zIndex: 50 }}
             className="flex items-center gap-1.5 rounded-full bg-black/70 text-white/80 text-xs px-2.5 py-1 backdrop-blur">
          <WifiOff className="w-3.5 h-3.5" /> Sin conexión — mostrando lo último
        </div>
      )}
    </div>
  );
}
